'use client'

import * as React from 'react'
import { cn } from '../lib/utils'

/**
 * FO-367 (batch-42) — RotatiePatroonEditor + RotatiePatroonStrip.
 *
 * Eén gedeelde, visuele ploegrotatie-bouwer: de gebruiker stelt het patroon
 * samen door per dag een dienst te kiezen uit gelabelde blokken
 * (Vroeg/Laat/Nacht/Dag/Vrij), met kleur per diensttype en de cycluslengte als
 * gevolg van het aantal dagen. Geen lettercodes typen; het component levert een
 * `DayCode[]`.
 *
 * Ingezet in ZOWEL de publieke klant-wizard (`apps/klant`, next-intl-labels via
 * props) ALS de backoffice-werktijden-tab (`apps/backoffice`, NL-defaults) —
 * één editor, geen drift. Om die dubbele inzet te dragen bevat dit component
 * GEEN i18n-dependency: alle zichtbare tekst komt binnen via `labels`, en de
 * presetlijst via `presets` (zie `getRotatiePatroonPresets` in `@streep/utils`).
 *
 * Mobiel (375px): de dag-blokken wrappen, tap-targets zijn ≥ 44px en de
 * voorbeeld-strip scrollt binnen zijn eigen container (geen page-scroll).
 */

export type DayCode = 'V' | 'L' | 'N' | 'D' | 'X'

/** Alle DayCodes in vaste volgorde (palet-volgorde). */
export const DAY_CODE_ORDER: readonly DayCode[] = ['V', 'L', 'N', 'D', 'X']

/**
 * Kleur per dienst — 1-op-1 gelijk aan de (oude) backoffice-roster-editor, zodat
 * de weergave herkenbaar blijft. STREEP-tokens: Dag = groen, Nacht = donker.
 */
export const DAG_CODE_KLEUR: Record<DayCode, { bg: string; text: string }> = {
  V: { bg: '#FFD79B', text: '#1A1A18' },
  L: { bg: '#9BD0FF', text: '#1A1A18' },
  N: { bg: '#1A1A18', text: '#FFFFFF' },
  D: { bg: '#CCFA78', text: '#1A1A18' },
  X: { bg: '#F0F1EC', text: '#5C5C58' },
}

export interface RotatiePatroonPresetOptie {
  /** Stabiele sleutel (React-key). */
  code: string
  /** Het patroon dat de preset toepast. */
  pattern: DayCode[]
  /**
   * Leesbaar label op de knop (bv. "Vroeg → Laat → Nacht (per week)"). Zonder
   * label toont de knop alleen de mini-strip + cycluslengte — bij meerdere
   * even lange presets zijn die niet uit elkaar te houden, dus geef 'm mee.
   */
  label?: string
}

export interface RotatiePatroonLabels {
  /** Dienstlabel per DayCode: NL Vroeg/Laat/Nacht/Dag/Vrij, EN Early/Late/… */
  diensten: Record<DayCode, string>
  /** Kop boven de presetknoppen (bv. "Veelgebruikte roosters"). */
  presets: string
  /** Knoptekst "Dag toevoegen". */
  dagToevoegen: string
  /** Knoptekst "Dag verwijderen". */
  dagVerwijderen: string
  /** Woord "Week" (voor "Week 1", "Week 2", …). */
  week: string
  /** Woord "dag"/"dagen" — enkelvoud + meervoud. */
  dag: string
  dagen: string
  /** Korte uitleg onder het palet (bv. "Kies een dienst en tik op een dag"). */
  hint: string
  /** Tekst bij een leeg patroon. */
  leeg: string
}

export interface RotatiePatroonEditorProps {
  /** Huidig patroon (controlled). */
  value: DayCode[]
  onChange: (pattern: DayCode[]) => void
  labels: RotatiePatroonLabels
  /** Presetknoppen (leeg = geen presets tonen). */
  presets?: RotatiePatroonPresetOptie[]
  /** Minimum cycluslengte (default 1). */
  minDagen?: number
  /** Maximum cycluslengte (default 42). */
  maxDagen?: number
  disabled?: boolean
  /** id-prefix voor stabiele test-/aria-hooks. */
  idPrefix?: string
  className?: string
}

const DEFAULT_MIN = 1
const DEFAULT_MAX = 42

function dagenLabel(n: number, labels: RotatiePatroonLabels): string {
  return `${n} ${n === 1 ? labels.dag : labels.dagen}`
}

/**
 * Read-only strip die het patroon per week toont ("Week 1: V V L L N N X"),
 * met gekleurde blokjes. Gedeeld door de editor-preview, de klant-overzicht-stap
 * en de backoffice-review — zelfde herkenbare weergave overal. Scrollt
 * horizontaal binnen zijn eigen container (mobiel-veilig).
 */
export function RotatiePatroonStrip({
  pattern,
  labels,
  className,
}: {
  pattern: DayCode[]
  labels: Pick<RotatiePatroonLabels, 'diensten' | 'week'>
  className?: string
}) {
  if (pattern.length === 0) return null

  const weken: DayCode[][] = []
  for (let i = 0; i < pattern.length; i += 7) {
    weken.push(pattern.slice(i, i + 7))
  }

  return (
    <div className={cn('space-y-1.5 overflow-x-auto', className)}>
      {weken.map((week, wi) => (
        <div key={wi} className="flex items-center gap-2 whitespace-nowrap">
          <span className="w-14 shrink-0 text-xs font-medium text-[#5C5C58]">
            {labels.week} {wi + 1}
          </span>
          <div className="flex gap-1">
            {week.map((code, di) => {
              // Defensief: val terug op 'Vrij' voor een onverwachte (niet-DayCode)
              // waarde, zodat de gedeelde strip nooit crasht op corrupte data.
              const kleur = DAG_CODE_KLEUR[code] ?? DAG_CODE_KLEUR.X
              return (
                <span
                  key={di}
                  className="flex h-6 w-6 items-center justify-center rounded text-[11px] font-bold"
                  style={{ backgroundColor: kleur.bg, color: kleur.text }}
                  title={`${labels.week} ${wi + 1} - ${labels.diensten[code] ?? code}`}
                >
                  {code}
                </span>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

export function RotatiePatroonEditor({
  value,
  onChange,
  labels,
  presets = [],
  minDagen = DEFAULT_MIN,
  maxDagen = DEFAULT_MAX,
  disabled = false,
  idPrefix = 'rotatie',
  className,
}: RotatiePatroonEditorProps) {
  // Actieve "kwast": de dienst die een tik op een dag toepast. Start op Vroeg.
  const [kwast, setKwast] = React.useState<DayCode>('V')

  const zetDag = (index: number, code: DayCode) => {
    if (disabled) return
    onChange(value.map((c, i) => (i === index ? code : c)))
  }

  const voegDagToe = () => {
    if (disabled || value.length >= maxDagen) return
    onChange([...value, kwast])
  }

  const verwijderDag = () => {
    if (disabled || value.length <= minDagen) return
    onChange(value.slice(0, -1))
  }

  const pasPresetToe = (preset: RotatiePatroonPresetOptie) => {
    if (disabled) return
    onChange(preset.pattern.slice(0, maxDagen))
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Presets */}
      {presets.length > 0 ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wide text-[#5C5C58]">
            {labels.presets}
          </p>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset.code}
                type="button"
                onClick={() => pasPresetToe(preset)}
                disabled={disabled}
                className="flex min-h-[44px] items-center gap-2 rounded-md border border-[#CED0C8] bg-white px-3 py-1.5 text-left transition-colors hover:border-[#1A1A18] disabled:opacity-50"
              >
                <span className="flex gap-0.5">
                  {preset.pattern.slice(0, 14).map((code, i) => (
                    <span
                      key={i}
                      className="h-4 w-2.5 rounded-[2px]"
                      style={{ backgroundColor: DAG_CODE_KLEUR[code].bg }}
                    />
                  ))}
                </span>
                {preset.label ? (
                  <span className="flex flex-col">
                    <span className="text-xs font-medium text-[#1A1A18]">{preset.label}</span>
                    <span className="text-[11px] text-[#5C5C58]">
                      {dagenLabel(preset.pattern.length, labels)}
                    </span>
                  </span>
                ) : (
                  <span className="text-xs font-medium text-[#5C5C58]">
                    {dagenLabel(preset.pattern.length, labels)}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Dienst-palet (gelabelde blokken) */}
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-2">
          {DAY_CODE_ORDER.map((code) => {
            const actief = kwast === code
            return (
              <button
                key={code}
                type="button"
                onClick={() => setKwast(code)}
                disabled={disabled}
                aria-pressed={actief}
                className={cn(
                  'flex min-h-[44px] items-center gap-2 rounded-md border-2 px-3 py-1.5 text-sm font-medium transition-all disabled:opacity-50',
                  actief ? 'border-[#1A1A18] ring-2 ring-[#CCFA78]' : 'border-[#CED0C8]',
                )}
              >
                <span
                  className="h-4 w-4 shrink-0 rounded"
                  style={{ backgroundColor: DAG_CODE_KLEUR[code].bg }}
                />
                {labels.diensten[code]}
              </button>
            )
          })}
        </div>
        <p className="text-xs text-[#5C5C58]">{labels.hint}</p>
      </div>

      {/* Dag-blokken */}
      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-[#CED0C8] bg-[#F0F1EC] px-3 py-4 text-center text-sm text-[#5C5C58]">
          {labels.leeg}
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {value.map((code, index) => {
            const kleur = DAG_CODE_KLEUR[code] ?? DAG_CODE_KLEUR.X
            const label = labels.diensten[code] ?? code
            return (
              <button
                key={index}
                type="button"
                onClick={() => zetDag(index, kwast)}
                disabled={disabled}
                aria-label={`${labels.dag} ${index + 1}: ${label}`}
                className="flex min-h-[44px] w-[3.25rem] flex-col items-center justify-center rounded-md border border-[#CED0C8] py-1 text-[11px] font-semibold leading-tight transition-transform hover:scale-105 disabled:hover:scale-100"
                style={{ backgroundColor: kleur.bg, color: kleur.text }}
              >
                <span className="opacity-60">{index + 1}</span>
                <span className="max-w-full truncate px-0.5">{label}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Cyclus +/- */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={verwijderDag}
          disabled={disabled || value.length <= minDagen}
          className="min-h-[44px] rounded-md border border-[#CED0C8] bg-white px-3 text-sm font-medium text-[#1A1A18] transition-colors hover:border-[#1A1A18] disabled:opacity-40"
        >
          − {labels.dagVerwijderen}
        </button>
        <button
          type="button"
          onClick={voegDagToe}
          disabled={disabled || value.length >= maxDagen}
          className="min-h-[44px] rounded-md border border-[#CED0C8] bg-white px-3 text-sm font-medium text-[#1A1A18] transition-colors hover:border-[#1A1A18] disabled:opacity-40"
        >
          + {labels.dagToevoegen}
        </button>
        <span className="text-xs text-[#5C5C58]">{dagenLabel(value.length, labels)}</span>
      </div>

      {/* Voorbeeld-strip */}
      {value.length > 0 ? (
        <div className="rounded-md border border-[#CED0C8] bg-[#F0F1EC] p-2.5">
          <RotatiePatroonStrip pattern={value} labels={labels} />
        </div>
      ) : null}
    </div>
  )
}
